import { Card, CardContent } from './ui/card';
import { ImageWithFallback } from './figma/ImageWithFallback';
import { Target, Eye, Users, Globe, Award, Heart } from 'lucide-react';

export function AboutPage() {
  const values = [
    {
      icon: <Award className="w-8 h-8 text-[#fe2b32]" />,
      title: "Quality Excellence",
      description: "We maintain the highest standards in material quality and processing."
    },
    {
      icon: <Heart className="w-8 h-8 text-[#ffc533]" />,
      title: "Trust & Reliability",
      description: "Building lasting partnerships through consistent and reliable service."
    },
    {
      icon: <Globe className="w-8 h-8 text-[#ff41a5]" />,
      title: "Global Reach",
      description: "Connecting markets worldwide with our extensive trading network."
    },
    {
      icon: <Users className="w-8 h-8 text-[#fe2b32]" />,
      title: "Customer Focus",
      description: "Putting our clients' needs first in everything we do."
    }
  ];

  const milestones = [
    {
      year: "2020",
      title: "Company Established",
      description: "DOUBLE CLICK TRADING FZE founded in Dubai with a vision to transform scrap trading."
    },
    {
      year: "2021",
      title: "International Expansion",
      description: "Extended operations to serve clients across the Globe."
    },
    {
      year: "2022",
      title: "Quality Certification",
      description: "Achieved international quality certifications and established quality control processes."
    },
    {
      year: "2023",
      title: "Technology Integration",
      description: "Implemented advanced tracking and logistics systems for improved service delivery."
    },
    {
      year: "2024",
      title: "Sustainable Growth",
      description: "Expanded our environmental sustainability initiatives and green recycling programs."
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-r from-gray-900 to-gray-800 text-white">
        <div className="container mx-auto px-4">
          <div className="text-center">
            <h1 className="text-5xl font-bold mb-6">About Double Click Trading FZE</h1>
            <p className="text-xl max-w-3xl mx-auto text-gray-200">
              Pioneering excellence in scrap trading since 2020 and connecting global markets.
            </p>
          </div>
        </div>
      </section>

      {/* Company Overview */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold mb-6">Our Story</h2>
              <p className="text-lg text-gray-600 mb-6">
                Since our inception in 2020, DOUBLE CLICK TRADING FZE has been driven by a bold vision: to transform the scrap trading industry through cutting-edge innovation, exceptional quality, and an unwavering commitment to exceeding customer expectations.
              </p>
              <p className="text-lg text-gray-600 mb-6">
                Situated in Dubai's vibrant commercial core, our strategic location empowers us to seamlessly connect markets across the Middle East, Asia, and beyond. We specialize in ferrous , non-ferrous and rubber scraps , delivering tailored trading solutions that align with the unique needs of our global clientele.
              </p>
              <p className="text-lg text-gray-600">
                What truly sets DOUBLE CLICK TRADING apart is our relentless dedication to integrity, transparency, and forging lasting partnerships. Every transaction is upheld by rigorous quality standards, competitive pricing, and a customer-centric approach — ensuring that our clients receive unmatched value and reliability every time they choose to work with us.
              </p>
            </div>
            <div>
              <ImageWithFallback 
                src="https://images.unsplash.com/photo-1676275773828-19040454afab?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMHRlYW0lMjBtZWV0aW5nJTIwb2ZmaWNlfGVufDF8fHx8MTc1NjI0NDM3OHww&ixlib=rb-4.1.0&q=80&w=1080"
                alt="Business Team Meeting" 
                className="rounded-lg shadow-lg w-full h-96 object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <Card className="p-8">
              <CardContent className="text-center">
                <div className="mb-6 flex justify-center">
                  <Target className="w-12 h-12 text-[#fe2b32]" />
                </div>
                <h3 className="text-3xl font-bold mb-4">Our Mission</h3>
                <p className="text-gray-600 text-lg">
                  To be the leading provider of high-quality scrap trading services, 
                  fostering sustainable recycling practices while delivering exceptional value 
                  to our clients through innovation, reliability, and excellence.
                </p>
              </CardContent>
            </Card>

            <Card className="p-8">
              <CardContent className="text-center">
                <div className="mb-6 flex justify-center">
                  <Eye className="w-12 h-12 text-[#ffc533]" />
                </div>
                <h3 className="text-3xl font-bold mb-4">Our Vision</h3>
                <p className="text-gray-600 text-lg">
                  To become the most trusted and innovative scrap trading company globally, 
                  setting benchmark industry standards for quality, sustainability, and customer satisfaction 
                  while contributing towards sustainable Environment & Growing Economy.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Company Values */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Our Core Values</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              The principles that guide our business operations and client relationships
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => {
              const gradients = [
                'from-[#fe2b32]/10 to-[#fe2b32]/5',
                'from-[#ffc533]/10 to-[#ffc533]/5',
                'from-[#ff41a5]/10 to-[#ff41a5]/5',
                'from-[#fe2b32]/10 to-[#fe2b32]/5'
              ];
              const borderColors = [
                'border-t-[#fe2b32]',
                'border-t-[#ffc533]',
                'border-t-[#ff41a5]',
                'border-t-[#fe2b32]'
              ];
              return (
                <Card 
                  key={index} 
                  className={`hover:shadow-2xl hover:scale-105 transition-all duration-300 border-t-4 ${borderColors[index]} bg-gradient-to-b ${gradients[index]}`}
                >
                  <CardContent className="p-8 text-center">
                    <div className="mb-6 flex justify-center transform hover:scale-110 transition-transform duration-300">
                      <div className="p-4 rounded-full bg-white shadow-lg">
                        {value.icon}
                      </div>
                    </div>
                    <h3 className="text-xl font-bold mb-3">{value.title}</h3>
                    <p className="text-gray-700 leading-relaxed">{value.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Company Timeline */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Our Journey</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Key milestones in our growth and development
            </p>
          </div>
          
          <div className="max-w-4xl mx-auto">
            <div className="space-y-8">
              {milestones.map((milestone, index) => (
                <div key={index} className="flex items-start">
                  <div className="flex-shrink-0 w-24 text-right pr-8">
                    <div className="text-2xl font-bold text-[#fe2b32]">{milestone.year}</div>
                  </div>
                  <div className="flex-shrink-0 w-4 h-4 bg-[#ffc533] rounded-full mt-2 mr-8"></div>
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold mb-2">{milestone.title}</h3>
                    <p className="text-gray-600">{milestone.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Statistics */}
      <section className="py-20 bg-gradient-to-r from-[#fe2b32] to-[#ff41a5] text-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">Our Impact</h2>
            <p className="text-xl max-w-3xl mx-auto">
              Numbers that reflect our commitment to excellence and growth
            </p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold mb-2">4+</div>
              <div>Years of Excellence</div>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold mb-2">50+</div>
              <div>Countries Served</div>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold mb-2">500+</div>
              <div>Happy Clients</div>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold mb-2">1000+</div>
              <div>Successful Trades</div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}